import { useState } from 'react';
import { DeleteOutlined, FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { StyledDragger } from 'components/new-ui/base/dragger/styles';
import FileIcon from 'components/new-ui/base/file-icon/FileIcon';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import useDeclarations from 'hooks/useDeclarations';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { bulkUploadItems, getBulkUploadItemsPresignedUrl } from 'store/products/client';
import { FileTypes } from 'utils/file-types';
import { getErrorMessage } from 'utils/getErrorMessage';
import { uploadFile } from 'utils/uploadFile';
import { AIS_MAX_ITEMS_PER_DECLARATION } from 'core/utils/constants';
import { isBeValidationError } from 'utils/common';
import BulkUploadItemsCapModal from './BulkUploadItemsCapModal';
import ErrorProcessingModal from './ErrorProcessingModal';
import FileErrorsModal from './FileErrorsModal';
import { ValidationWithSubErrors } from 'types/common';

type InternalView = 'upload' | 'error' | 'fileErrors';

type Props = {
    open: boolean;
    numberOfItemsInDeclaration: number | undefined;
    onClose: () => void;
    onSuccess: () => void;
    onItemCapExceeded: () => void;
};

const allowedFileTypes = [FileTypes.CSV, FileTypes.XLSX, FileTypes.XLS];
const maxFileSizeInBytes = 5 * 1024 * 1024; //5 MB

const isAcceptedFileType = (ext: string) => allowedFileTypes.map((f) => f.toLowerCase()).includes(ext);

const BulkUploadItemsModal = ({ open, onClose, onSuccess, onItemCapExceeded, numberOfItemsInDeclaration }: Props) => {
    const { declaration } = useDeclarations();
    const [file, setFile] = useState<RcFile | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [internalView, setInternalView] = useState<InternalView>('upload');
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [validationResponse, setValidationResponse] = useState<ValidationWithSubErrors | undefined>();
    const [capModalOpen, setCapModalOpen] = useState(false);
    const [uploadedItemsPriceSum, setUploadedItemsPriceSum] = useState<number | undefined>();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const handleClose = () => {
        setInternalView('upload');
        setFile(null);
        setErrorMessage(undefined);
        onClose();
    };

    const handleCreateItems = async () => {
        setIsSubmitting(true);
        showGlobalOverlay({
            type: 'LoadingOverlay',
            payload: {
                message: 'Creating items...',
            },
        });

        if (!declaration?.id) {
            Notification({
                type: 'error',
                messageTitle: 'Internal Error',
                description:
                    'Something went wrong on our end. Please try again and let us know if the problem persist.',
                placement: 'top',
            });
            return;
        }

        if (!file) {
            Notification({
                type: 'error',
                messageTitle: 'File not found',
                description: `Could not detect the file that you're trying to send.`,
                placement: 'top',
            });
            return;
        }

        try {
            // Get the presigned URL
            const { preSignedUrl } = await getBulkUploadItemsPresignedUrl(declaration.id, file.name, file.size);

            // Sign the file
            await uploadFile(file, preSignedUrl);

            // Extract the s3 link from the url
            const s3Link = decodeURIComponent(new URL(preSignedUrl).pathname.slice(1));

            // Send the request to the BE
            const response = await bulkUploadItems(declaration.id, s3Link);

            if (!response.numberOfItems || response.numberOfItems < 1) {
                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: 'Could not create items from the uploaded file. Please try again.',
                    placement: 'top',
                });
                return;
            }

            onClose();
            onSuccess();
            setFile(null);

            // There is a limit of 150 items that can be in a single declaration for AIS,
            // so the BE creates new draft declarations that will include the rest of the products
            const hasExceededMaxItemsPerDeclaration =
                numberOfItemsInDeclaration &&
                numberOfItemsInDeclaration + response.numberOfItems > AIS_MAX_ITEMS_PER_DECLARATION;

            if (response.numberOfItems > AIS_MAX_ITEMS_PER_DECLARATION || hasExceededMaxItemsPerDeclaration) {
                setUploadedItemsPriceSum(response.itemsPriceSum);
                setCapModalOpen(true);
            } else {
                Notification({
                    type: 'success',
                    messageTitle: `${response.numberOfItems} Products Created`,
                    description: `${response.numberOfItems} products have been successfully added to declaration.`,
                    placement: 'top',
                });
            }
        } catch (error) {
            setFile(null);
            if (isBeValidationError(error)) {
                setInternalView('fileErrors');
                setValidationResponse(error.response!.data);
            } else {
                setErrorMessage(getErrorMessage(error));
                setInternalView('error');
            }
        } finally {
            setIsSubmitting(false);
            hideGlobalOverlay();
        }
    };

    const beforeUpload = async (file: RcFile) => {
        const fileSize = file.size;
        const ext = file.name.split('.').at(-1)?.toLowerCase();

        if (fileSize > maxFileSizeInBytes) {
            Notification({
                type: 'error',
                messageTitle: 'File size exceeds the limit',
                description: 'The selected file must not exceed 5 MB.',
                placement: 'top',
            });
            setFile(null);
            return false;
        }

        if (!ext || !isAcceptedFileType(ext)) {
            Notification({
                type: 'error',
                messageTitle: 'Unsupported file type',
                description: 'Only CSV, XLS, and XLSX files are supported.',
                placement: 'top',
            });
            setFile(null);
            return false;
        }

        setFile(file);
        return false;
    };

    return (
        <>
            <ConfirmationModal
                maskClosable={false}
                open={open && internalView === 'upload'}
                onConfirm={handleCreateItems}
                icon={<FileTextOutlined />}
                title="Bulk Upload"
                confirmButtonText="Create Items"
                confirmationButtonDisabled={!file}
                confirmationButtonLoading={isSubmitting}
                onCancel={handleClose}
                onClose={handleClose}
                content={
                    <div style={{ width: '100%', marginTop: 8 }}>
                        <StyledDragger multiple={false} showUploadList={false} beforeUpload={beforeUpload}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                }}
                            >
                                <InboxOutlined style={{ fontSize: 24, color: 'var(--brand-6)' }} />
                                <span>Click or drag file to this area to upload</span>
                            </div>
                        </StyledDragger>

                        {file && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: 16,
                                }}
                            >
                                <Space size={6}>
                                    <FileIcon type="xlsx" style={{ fontSize: 16 }} />
                                    <span>{file.name}</span>
                                </Space>
                                <IconButton
                                    icon={<DeleteOutlined />}
                                    tooltipText="Delete file"
                                    onClick={() => setFile(null)}
                                />
                            </div>
                        )}
                    </div>
                }
            />

            <ErrorProcessingModal
                open={open && internalView === 'error'}
                errorNotification={errorMessage ?? ''}
                onClose={handleClose}
                onConfirm={() => {
                    setErrorMessage(undefined);
                    setInternalView('upload');
                }}
            />

            {validationResponse && (
                <FileErrorsModal
                    open={open && internalView === 'fileErrors'}
                    validationResponse={validationResponse}
                    onClose={handleClose}
                    onConfirm={() => {
                        setValidationResponse(undefined);
                        setInternalView('upload');
                    }}
                />
            )}

            <BulkUploadItemsCapModal
                open={capModalOpen}
                itemsPriceSum={uploadedItemsPriceSum}
                currency={declaration?.irelandImportDeclaration?.invoiceCurrency}
                onClose={() => setCapModalOpen(false)}
                onConfirm={() => {
                    setCapModalOpen(false);
                    onItemCapExceeded();
                }}
            />
        </>
    );
};

export default BulkUploadItemsModal;
